import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Truck, MapPin, Thermometer, Network, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useImages } from '../hooks/useImages';

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const EcoRoute = () => {
  const siteImages = useImages();
  const img = (slug, fb) => siteImages[slug] || fb;

  const features = [
    { icon: MapPin, title: 'Route Optimization', desc: 'AI-powered logistics for fastest delivery' },
    { icon: Thermometer, title: 'Cold Chain', desc: 'Temperature-controlled transport for freshness' },
    { icon: Network, title: 'Farmer Connect', desc: 'Direct market linkage for better prices' },
    { icon: Truck, title: 'Real-Time Tracking', desc: 'Track your shipment every step of the way' }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FDF8F0' }}>
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4A574]/8 to-[#5C7A3A]/5"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial="hidden" animate="visible" variants={stagger}>
            <div className="space-y-6">
              <motion.span variants={fadeUp} custom={0} className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#C4844A] bg-[#C4844A]/8 px-4 py-2 rounded-full border border-[#C4844A]/15">
                AI Logistics &middot; Zero Waste &middot; Farmer-First
              </motion.span>
              <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Eco<span className="text-[#C4844A]">Route</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-base text-[#5a4535] leading-relaxed max-w-lg">
                Smart logistics that connects farmers directly to markets. Our AI-optimized cold chain ensures freshness while reducing waste and maximizing farmer income.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Join EcoRoute Network', '_blank')} className="bg-[#C4844A] hover:bg-[#A96E3A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-[#C4844A]/20 transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="eco-join-btn">
                  Join Network <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Partnership Inquiry', '_blank')} className="border-2 border-[#C4844A] text-[#C4844A] hover:bg-[#C4844A] hover:text-white font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="eco-partner-btn">
                  Partner With Us
                </Button>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} custom={2}>
              <img src={img('eco-hero', 'https://images.unsplash.com/photo-1761574319274-8a3a9cd80a6e')} alt="EcoRoute logistics" className="w-full h-80 object-cover rounded-2xl shadow-xl shadow-[#C4844A]/10" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16"></div>
              <Truck size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16"></div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>How EcoRoute Works</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 text-center bg-[#FDF8F0]" data-testid={`eco-feature-${idx}`}>
                  <CardContent className="p-6">
                    <div className="bg-[#C4844A]/10 p-3.5 rounded-2xl inline-block mb-4 group-hover:bg-[#C4844A]/15 transition-colors duration-300">
                      <f.icon size={28} className="text-[#C4844A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#C4844A] transition-colors duration-300">{f.title}</h3>
                    <p className="text-sm text-[#7a6555]">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Benefits for Stakeholders</motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'For Farmers', color: '#C4844A', items: ['Direct market access', 'Better price realization', 'Reduced spoilage', 'Timely payments'] },
              { title: 'For Buyers', color: '#5C7A3A', items: ['Fresh farm produce', 'Reliable delivery', 'Quality assurance', 'Transparent pricing'] }
            ].map((group, gi) => (
              <motion.div key={gi} initial={{ opacity: 0, x: gi === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: gi * 0.15, duration: 0.5 }} className="bg-white p-7 rounded-2xl border border-[#E8DFD3] shadow-md hover:shadow-lg transition-shadow duration-300" data-testid={`benefit-${gi}`}>
                <h3 className="text-xl font-bold mb-5" style={{ color: group.color, fontFamily: 'Playfair Display, serif' }}>{group.title}</h3>
                <div className="space-y-3">
                  {group.items.map((b, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle size={18} className="mt-0.5 flex-shrink-0" style={{ color: group.color }} />
                      <p className="text-base text-[#2C1810]">{b}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C1810] to-[#4a3020]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Join the <span className="text-[#D4A574]">EcoRoute Network</span></h2>
            <p className="text-base text-white/75 mb-8 max-w-md mx-auto">Whether you're a farmer or buyer, connect with us for seamless logistics</p>
            <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Join EcoRoute', '_blank')} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="eco-getstarted-btn">
              Get Started <ArrowRight className="ml-2" size={18} />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EcoRoute;
